/**
 * @file CategoryTreeSidebar.tsx
 * @description Боковая панель категорий реестра товаров (phase14.0.1;
 * phase19.31 — только на очень широком экране, с 1600px, SC52).
 *
 * «Все» сверху, дерево ниже: стрелка раскрывает ветку (и не выбирает),
 * название выбирает категорию вместе с потомками. Число — товаров в
 * категории вместе с подкатегориями (`productsCountTotal` сервера, контракт
 * §20); если в подкатегориях есть товары — подсказка «всего / в самой». Подписи без эмодзи и капса (SC29, SC43);
 * внешние площадки (Авито и др.) — отдельной группой «Каналы продаж».
 */

import { ChevronRight } from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";

import type { CatalogCategory, CatalogProduct } from "../../lib/api-catalog";
import {
  buildCategoryTree,
  categoryCountHint,
  categoryLabel,
  categoryProductCounts,
  type CategoryTreeNode,
} from "../../lib/catalog-category-tree";
import { cn } from "../../lib/utils";

interface Props {
  categories: CatalogCategory[];
  products: CatalogProduct[];
  selectedCategoryId: string | null;
  onSelect: (id: string | null) => void;
  className?: string;
}

export function CategoryTreeSidebar({ categories, products, selectedCategoryId, onSelect, className }: Props) {
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const roots = useMemo(() => buildCategoryTree(categories), [categories]);
  const internal = roots.filter((n) => !n.isExternal);
  const external = roots.filter((n) => n.isExternal);

  function toggle(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function select(node: CategoryTreeNode) {
    // Выбрали ветку — раскрываем её, чтобы были видны подкатегории.
    if (node.children.length > 0) setExpanded((prev) => new Set(prev).add(node.id));
    onSelect(node.id);
  }

  const branch = (nodes: CategoryTreeNode[]): ReactNode =>
    nodes.map((node) => (
      <li key={node.id}>
        <Row
          node={node}
          open={expanded.has(node.id)}
          active={selectedCategoryId === node.id}
          onToggle={() => toggle(node.id)}
          onSelect={() => select(node)}
        />
        {node.children.length > 0 && expanded.has(node.id) ? <ul>{branch(node.children)}</ul> : null}
      </li>
    ));

  return (
    <nav aria-label="Категории" className={cn("flex flex-col gap-1", className)}>
      <p className="type-caption px-3 pb-1 text-on-surface-variant">Категории</p>
      <button
        type="button"
        onClick={() => onSelect(null)}
        aria-current={selectedCategoryId === null || undefined}
        className={cn(ROW_CLASS, "px-3", selectedCategoryId === null ? ROW_ACTIVE : ROW_IDLE)}
      >
        <span className="font-medium">Все</span>
        <span className="tabular-nums text-on-surface-variant">{products.length}</span>
      </button>
      <ul>{branch(internal)}</ul>
      {external.length > 0 ? (
        <>
          <p className="type-caption mt-3 px-3 pb-1 text-on-surface-variant">Каналы продаж</p>
          <ul>{branch(external)}</ul>
        </>
      ) : null}
    </nav>
  );
}

const ROW_CLASS = "type-body flex min-h-9 w-full items-center justify-between gap-2 rounded-md text-left transition-colors";
const ROW_ACTIVE = "bg-accent text-accent-foreground";
const ROW_IDLE = "text-on-surface hover:bg-surface-low";

interface RowProps {
  node: CategoryTreeNode;
  open: boolean;
  active: boolean;
  onToggle: () => void;
  onSelect: () => void;
}

function Row({ node, open, active, onToggle, onSelect }: RowProps) {
  const hasChildren = node.children.length > 0;
  const counts = categoryProductCounts(node);
  const hint = categoryCountHint(counts);
  return (
    <div className={cn("flex items-start rounded-md", active ? ROW_ACTIVE : ROW_IDLE)} style={{ paddingLeft: `${node.depth * 16}px` }}>
      <button
        type="button"
        onClick={onToggle}
        disabled={!hasChildren}
        aria-label={open ? "Свернуть" : "Раскрыть"}
        aria-expanded={hasChildren ? open : undefined}
        className={cn("flex size-8 shrink-0 items-center justify-center text-on-surface-variant", !hasChildren && "invisible")}
      >
        <ChevronRight className={cn("size-4 transition-transform", open && "rotate-90")} />
      </button>
      <button type="button" onClick={onSelect} aria-current={active || undefined} className={cn(ROW_CLASS, "min-w-0 flex-1 pr-3")}>
        <span className={cn("line-clamp-2 min-w-0 py-1.5 [overflow-wrap:normal]", active && "font-medium")}>{categoryLabel(node)}</span>
        <span className="shrink-0 self-center text-right tabular-nums text-on-surface-variant" title={hint ?? undefined}>
          {counts?.total ?? 0}
          {hint ? <span className="sr-only"> ({hint})</span> : null}
        </span>
      </button>
    </div>
  );
}
